/**
 * The PDF of an external DeCA, chosen with the button or dropped on the zone around it (#225).
 *
 * Paco's users take the PDF from a mail: a desktop mail program hands a dragged attachment to the
 * page as a file, so it can land here without being saved first. Webmail hands over a link, not a
 * file, and the zone says so when nothing arrives.
 *
 * **One way in.** The button's file input and the drop both go through `take()`, so a dropped PDF
 * meets exactly the checks a chosen one does (`pdfRefusal`) and reaches the same `onFile`, and
 * the screen's request cannot tell the two apart. What is refused is said on the zone, where the
 * file went, and nothing else changes: a PDF chosen earlier stays chosen and nothing is sent.
 *
 * The zone is not a button, as the Excel import's is, because it holds one: a `<button>` cannot
 * contain another. The button is the keyboard's way in; the zone is for the pointer.
 *
 * A file dropped *beside* the zone is the browser's to open, and it would open the PDF in place of
 * the portal. `useDroppedFilesStayOnPage` stops that for as long as the screen is open.
 */

import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { pdfRefusal } from '../app/externalDecas.ts'
import { Icon } from './Icon.tsx'
import { Button } from './primitives.tsx'

export function PdfDropZone({
  label,
  busy = false,
  loading = false,
  onFile,
  children,
}: {
  /** The button's own words: «Elegir el PDF», «Adjuntar el PDF», «Sustituir la copia». */
  label: string
  /** While the screen's request is on its way: the button is off and a drop is not taken. */
  busy?: boolean
  loading?: boolean
  onFile(file: File): void
  /** Beside the button: the file the form holds, or a line of help. */
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  function take(files: ArrayLike<File>) {
    const problem = pdfRefusal(files)
    setRefusal(problem)
    if (!problem) onFile(files[0]!)
  }

  // `preventDefault` is what tells the browser this element takes the drop; without it `drop` never
  // fires. `dropEffect` is left as the source allows, since forcing «copy» on a drag that offers
  // only «move» would refuse it.
  const accept = (event: DragEvent) => {
    if (busy) return
    event.preventDefault()
    setOver(true)
  }

  return (
    <div
      className="dropzone dropzone--inline"
      data-over={over}
      data-disabled={busy}
      onDragEnter={accept}
      onDragOver={accept}
      onDragLeave={(event) => {
        // Crossing from the zone onto the button inside it is not leaving.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        setOver(false)
      }}
      onDrop={(event) => {
        if (busy) return
        event.preventDefault()
        setOver(false)
        take(event.dataTransfer.files)
      }}
    >
      <div className="row wrap">
        <span>{t('externalDecas.dropHint')}</span>
        <Button icon="upload" loading={loading} disabled={busy} onClick={() => input.current?.click()}>
          {label}
        </Button>
        {children}
      </div>
      {refusal ? (
        <span className="field__error" role="alert">
          <Icon name="alert" size={14} />
          {t(refusal)}
        </span>
      ) : null}
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          // Emptied at once, so the same file chosen again is a change again.
          event.target.value = ''
          // A picker closed without a choice is not a refusal.
          if (files.length > 0) take(files)
        }}
      />
    </div>
  )
}

/**
 * While the calling screen is open, a file dropped anywhere but on a zone is not opened by the
 * browser, which would leave the portal for the PDF.
 *
 * A zone that takes the drop has already prevented its default, so it is left alone. A drag of
 * text or a link is left alone over a field, where dropping it is typing; anywhere else it would
 * navigate to the link just the same — webmail's attachment is one — so it is stopped too.
 * `dropEffect = 'none'` is what shows the pointer that it cannot be dropped there.
 */
export function useDroppedFilesStayOnPage() {
  useEffect(() => {
    function guard(event: globalThis.DragEvent) {
      if (event.defaultPrevented) return
      const files = event.dataTransfer?.types.includes('Files') ?? false
      if (!files && isEditable(event.target)) return
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
    }
    window.addEventListener('dragover', guard)
    window.addEventListener('drop', guard)
    return () => {
      window.removeEventListener('dragover', guard)
      window.removeEventListener('drop', guard)
    }
  }, [])
}

function isEditable(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || target.matches('input, textarea'))
}
